import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {leaveLobby, loadState, resetState, showToast, updateLobby,
} from '../../redux/actions';
import {getOwnMatchResultProfileActions} from "../../utils/gameEnd/getOwnMatchResultProfileActions";
import {closeNetworkSocket, notifyNetworkMessageSubscribers, setNetworkSocket} from '../../utils/networkSocket';
import PageButton from '../elements/PageButton/PageButton';
import {
  clearExpectingClose,
  isExpectingClose,
  markExpectingClose,
  triggerConnectionLost,
  triggerMidGameReconnected,
  triggerReconnectStatus,
  triggerSessionSuperseded
} from '../../utils/connectionMonitor';
import './LobbyPage.css';

import {API_BASE, WS_BASE} from '../../utils/apiConfig';
import {SCREENS_LOBBY} from "../../constants/screens";
import {applyRelayedServerAction} from "../../utils/network/applyRelayedServerAction";
import {getStaleActionRejectedEventValue} from "../../utils/network/staleActionRejection";
import FactionSelectPage from "../pages/FactionSelectPage/FactionSelectPage";
import LobbySelectFieldPage from "../pages/LobbySelectFieldPage/LobbySelectFieldPage";
import FieldPlayersMismatchModal from "../Modals/FieldPlayersMismatchModal/FieldPlayersMismatchModal";
import LobbyInviteModal from "../Modals/LobbyInviteModal/LobbyInviteModal";
import {PRIMARY_COLOR} from "../../styles/constants";
import {MENU_SOUND_MANAGER} from "../../scenes/MenuSoundManager";
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import {sendButtonClickEvent, sendNetworkEvent} from "../../analytics/gameAnalytics";
import store from "../../redux/store";
import {selectIsRankedMatch} from "../../redux/reducers/sharedStateReducer/selectors";
import PlayerFactionIcon from "../PlayerFactionIcon/PlayerFactionIcon";
import LobbyPlayerName from "./LobbyPlayerName";
import LobbyBonusToggle from "./LobbyBonusToggle";
import {RANDOM_FACTION_ID} from "../../configs/factions";
import {getRankSkinFramePath} from "../../utils/factions/getRankSkinFramePath";
import {useObservedHeight} from "../../hooks/visuals/useObservedHeight";
import {useImagePreload} from "../../hooks/react/useImagePreload";
import {IMAGE_LOAD_STATUS, loadImageOnce} from "../../utils/images/imageLoadCache";
import {getLobbyPreloadPaths, getOwnLobbyFactionArtPath} from "./lobbyArtPaths";
import LobbyCountdownSlot from "./LobbyCountdownSlot";
import {getLobbySubtitle, getLobbyTitle} from "./lobbyHeading";
import LobbyKickConfirmModal from "../Modals/LobbyKickConfirmModal/LobbyKickConfirmModal";
import {selectServerTimeDelta} from "../../redux/reducers/privateStateReducer/selectors";
import {
  checkIfGuestJoinRefused,
  getLobbyNetworkIdentity,
  GUEST_SPECTATE_UNAVAILABLE_MESSAGE,
} from "../../utils/lobby/guestViewer";
import FactionCardOverlay from "../FactionCardOverlay/FactionCardOverlay";
import RankedPhaseHintModal from "../Modals/RankedPhaseHintModal/RankedPhaseHintModal";
import AiLobbySpectatorModal from "../Modals/AiLobbySpectatorModal/AiLobbySpectatorModal";
import {getAiLobbyCountdownState} from "../../utils/aiMatch/getAiLobbyCountdownState";
import {checkIfAiLobbyLeaveAllowed} from "../../utils/aiMatch/checkIfAiLobbyLeaveAllowed";
import {
  checkIfAiLobbySpectator,
  checkIfAiLobbySpectatorNoticeDue,
} from "../../utils/aiMatch/checkIfAiLobbySpectatorNoticeDue";
import {applyMatchChatServerMessage} from "../../utils/chat/applyMatchChatServerMessage";
import {AI_PROMO_JOIN_REASONS, AI_PROMO_MAX_PLAYERS} from "../../utils/aiPromo/aiPromoJoin";
import {AI_PROMO_STATUSES} from "../../configs/aiMatch";
import {TEXT_AI_LOBBY_AUTH_REQUIRED, TEXT_AI_MATCH_CANCELLED_TODAY} from "../../constants/text/aiMatch";
import {
  checkIfRankedPhaseStarted,
  getBanVoterIdsForFaction,
  getFactionBanVoterIds,
  getMissingBanTargetIds,
} from "../../utils/lobby/factionBans";
import {checkIfFactionConfirmRequired} from "../../utils/lobby/rankFactionConfirm";
import {useDesktopLayout} from "../../hooks/visuals/useWideScreenLayout";
import LobbyBanStatusBadge, {LOBBY_BAN_STATUSES} from "./LobbyBanStatusBadge";
import {getLobbyCountdownUrgency, LOBBY_COUNTDOWN_URGENCY} from "./lobbyCountdownUrgency";
import LobbyDesktop from "./desktop/LobbyDesktop";

// Во время матча внезапный обрыв сокета (сворачивание на мобильных, короткая
// просадка сети) не должен сразу рушить приложение полным reload — сначала
// пробуем переподключиться на месте несколько раз (первая попытка сразу,
// дальше экспоненциальный бэкофф), переиспользуя серверный ресинк состояния
// через LOAD_STATE. Только когда попытки исчерпаны, отдаем обрыв в
// triggerConnectionLost (см. эффект ниже)
const MID_GAME_RECONNECT_DELAYS_MS = [0, 1000, 2000, 4000, 8000];

// Сторож тишины. Сервер шлет PING каждые 10 секунд (см. HEARTBEAT_INTERVAL_MS
// в server/index.js), поэтому пропущенные подряд импульсы означают, что канал
// умер молча: при полуоткрытом соединении (и при оффлайне на устройстве) close
// не приходит НИКОГДА, и без этой проверки игрок до конца партии смотрел бы на
// замерзшую доску без всякого признака обрыва.
//
// Реакция двухступенчатая. Сначала — честная плашка «сервер не отвечает»:
// поле уже замерло, и игрок должен узнать об этом сразу, а не гадать, почему
// соперник думает так долго. Если тишина продолжается — закрываем сокет сами,
// дальше работает штатный авто-реконнект с серверным ресинком состояния
const SERVER_SILENCE_HINT_MS = 15000;
const SERVER_SILENCE_LIMIT_MS = 25000;
const SILENCE_CHECK_INTERVAL_MS = 2500;

// Окно, в котором второе нажатие по иконке фракции считается двойным. Браузер
// присылает перед dblclick два обычных click, поэтому одиночное действие
// (голос за бан) откладывается на это время и отменяется, если двойное
// пришло: иначе на сервер уходили бы два BAN_FACTION подряд, и второй снимал
// бы голос, поставленный первым
const FACTION_ICON_DOUBLE_CLICK_MS = 250;

// Сколько держится оптимистичный голос (и подтверждение) фазы банов, пока
// сервер не прислал в дескрипторе то же значение. Не до первого же
// LOBBY_UPDATE: чужое обновление, ушедшее с сервера раньше эха нашего
// голоса, стерло бы только что поставленную метку. Если сервер голос так и
// не принял (отказ ERROR), метка по истечении срока возвращается к его
// значению. Тот же срок — у подтверждения выбора фракции
const OPTIMISTIC_BAN_STATE_TTL_MS = 3000;

// «Подтвердить баны» и «Изменить баны» (как и «Подтвердить выбор» и
// «Изменить выбор» фракции) — одна кнопка-переключатель: второй щелчок
// двойного нажатия (или двойной тап) тут же снял бы только что поставленное
// подтверждение. Повторное переключение раньше этого срока — мимо
const CONFIRM_TOGGLE_GUARD_MS = 500;

// Сколько целых секунд до дедлайна по часам сервера (отсчеты фаз лобби)
const getCountdownSecondsLeft = (deadlineAt, serverTimeDelta) => (
  Math.max(0, Math.ceil((deadlineAt - (Date.now() + serverTimeDelta)) / 1000))
);

// Эффект «выбрано» на арте фракции во встроенном выборе десктопного лобби
const FACTION_SELECT_FLASH_MS = 1100;

// Правила текущей фазы. Показываются двумя способами с одним и тем же
// текстом: подсказкой по наведению на иконку и модалкой по нажатию на нее —
// на тачскрине наведения нет, и title там не покажется никогда
const FACTION_BAN_HINT = {
  title: 'Баны фракций',
  lines: [
    'Каждый вычеркивает по одной фракции каждому сопернику — нажмите на фракцию соперника.',
    'Свои фракции банить нельзя.',
    'Голоса видны всем участникам лобби: число на фракции — сколько игроков за нее проголосовало.',
    'Боты со значком рядом с ником не банят никого — их фракции банят наравне с остальными.',
    'Повторное нажатие снимает голос, нажатие на другую фракцию того же соперника переносит его туда.',
    'Выбрав баны, нажмите «Подтвердить баны». Чтобы переголосовать, нажмите «Изменить баны».',
    'Фаза заканчивается, когда подтвердят все или выйдет время: у каждого вычеркнется фракция, набравшая больше всего голосов.',
    'Двойное нажатие на фракцию открывает карточку с ее подробным описанием.',
  ],
};

// Выбор фракции рейтингового лобби фиксируется отдельной кнопкой (см.
// utils/lobby/rankFactionConfirm) — одинаково на телефоне и десктопе
const FACTION_SELECT_CONFIRM_HINT_LINES = [
  'Выбор фиксирует кнопка «Подтвердить выбор». До подтверждения фракцию можно менять сколько угодно, после — только нажав «Изменить выбор».',
  'Матч начнется, когда подтвердят все, или когда выйдет время. Не успели подтвердить — сыграете выбранной фракцией, не выбрали никакой — случайной незабаненной.',
];

const FACTION_SELECT_HINT = {
  title: 'Выбор фракции',
  lines: [
    'Выберите фракцию кнопкой «Выбрать фракцию» или прямо из карточки своей незабаненной фракции.',
    ...FACTION_SELECT_CONFIRM_HINT_LINES,
    'Нажатие на иконку фракции — хоть своей, хоть соперника — открывает карточку с ее подробным описанием.',
  ],
};

// На десктопе голоса подписаны никами, а у каждой фракции — своей и
// соперника — есть кнопка «i» с карточкой
const FACTION_BAN_HINT_DESKTOP = {
  ...FACTION_BAN_HINT,
  lines: FACTION_BAN_HINT.lines.map((line) => {
    if (line.startsWith('Голоса видны всем')) {
      return 'Голоса видны всем участникам лобби: на фракции подписано, кто за нее проголосовал.';
    }
    if (line.startsWith('Двойное нажатие')) {
      return 'Кнопка «i» на фракции — своей или соперника — или двойное нажатие открывает карточку с ее описанием.';
    }
    return line;
  }),
};

// На десктопе своя фракция выбирается прямо нажатием по плитке
const FACTION_SELECT_HINT_DESKTOP = {
  title: 'Выбор фракции',
  lines: [
    'Нажмите на свою фракцию, чтобы выбрать ее. Забаненную выбрать нельзя.',
    ...FACTION_SELECT_CONFIRM_HINT_LINES,
    'Кнопка «i» на плитке или двойное нажатие открывает карточку фракции с подробным описанием.',
    'Нажатие на фракцию соперника (или «i» на ней) открывает ее карточку.',
  ],
};

/**
 * Оптимистичные голоса, которые еще ждут сервера: снимаются, когда в
 * дескрипторе пришло то же значение, или по истечении срока. Без изменений
 * возвращает тот же объект — эффекту не на что перерисовывать.
 *
 * @param {Object} pendingVotes - {[цель]: {factionId, sentAt, serverFactionIdAtSend}}
 * @param {Object} serverVotes - свои голоса из дескриптора, {[цель]: фракция}
 * @param {number} now
 * @returns {Object}
 */
const pruneOptimisticBanVotes = (pendingVotes, serverVotes, now) => {
  // Совпадение с сервером — эхо, только если на момент отправки у сервера было
  // другое значение. Вернулся к прежнему (A→B→A, поставил и снял) — совпадение
  // ничего не доказывает: сервер еще не принял промежуточный голос, и его эхо
  // мигнуло бы поверх. Такой голос держится до срока
  const keptEntries = Object.entries(pendingVotes).filter(([targetPlayerId, vote]) => (
    now - vote.sentAt < OPTIMISTIC_BAN_STATE_TTL_MS
    && (vote.serverFactionIdAtSend === (vote.factionId || null)
      || (serverVotes[targetPlayerId] || null) !== (vote.factionId || null))
  ));
  return keptEntries.length === Object.keys(pendingVotes).length ? pendingVotes : Object.fromEntries(keptEntries);
};

/**
 * Сколько еще держать оптимистичное подтверждение (банов или выбора
 * фракции): 0 — пришло эхо сервера или вышел срок. Эхо — только если сервер
 * на момент отправки считал иначе (см. pruneOptimisticBanVotes).
 *
 * @param {Object} pendingConfirmation - {isConfirmed, sentAt, serverIsConfirmedAtSend}
 * @param {boolean} isServerConfirmed - что сейчас в дескрипторе
 * @param {number} now
 * @returns {number}
 */
const getOptimisticConfirmationRemainingMs = (pendingConfirmation, isServerConfirmed, now) => {
  const isServerEcho = isServerConfirmed === pendingConfirmation.isConfirmed
    && pendingConfirmation.serverIsConfirmedAtSend !== pendingConfirmation.isConfirmed;
  return isServerEcho ? 0 : Math.max(0, pendingConfirmation.sentAt + OPTIMISTIC_BAN_STATE_TTL_MS - now);
};

// В подсказке по наведению заголовка нет — ставим его первой строкой
const getHintTitleAttribute = (hint) => [hint.title, ...hint.lines].join('\n');

// Причина LOBBY_CLOSED, когда сервер отменил матч дня против ИИ (см.
// utils/aiPromo). Тексты тостов ИИ-лобби — в constants/text/aiMatch.js
const AI_PROMO_CANCELLED_REASON = 'AI_PROMO_CANCELLED';

// В каких ИИ-лобби пользователь уже видел модалку «вы зритель»: один раз на
// лобби за сессию страницы, хотя LobbyPage перемонтируется (переход на экран
// выбора фракции и обратно, переподключение)
const shownAiSpectatorNoticeLobbyIds = new Set();

// Статусы промо-лобби, при которых набор игроков закрыт: идут баны и выбор
// фракций (STARTING) или уже сам матч
const AI_PROMO_RECRUITMENT_CLOSED_STATUSES = new Set([AI_PROMO_STATUSES.STARTING, AI_PROMO_STATUSES.GAME_STARTED]);

/**
 * Почему пользователь ИИ-лобби оказался зрителем — для текста модалки.
 * Места еще есть, а набор уже закрыт (баны, выбор фракций, сам матч) —
 * PHASE_STARTED; иначе лобби было полным — LOBBY_FULL. Выбор фракций после
 * банов виден по aiPromo.status: дедлайн банов к этому моменту уже снят, а
 * isFactionBanPhaseResolved сервер в дескрипторе сейчас не присылает.
 *
 * @param {Object} lobby - private.lobby
 * @returns {string} значение AI_PROMO_JOIN_REASONS
 */
const getAiLobbySpectatorReason = (lobby) => {
  const maxPlayers = lobby.aiPromo?.maxPlayers || AI_PROMO_MAX_PLAYERS;
  const isRecruitmentClosed = Boolean(lobby.factionBanDeadlineAt)
    || Boolean(lobby.isFactionBanPhaseResolved)
    || lobby.status === 'game_started'
    || AI_PROMO_RECRUITMENT_CLOSED_STATUSES.has(lobby.aiPromo?.status);
  return isRecruitmentClosed && (lobby.players || []).length < maxPlayers
    ? AI_PROMO_JOIN_REASONS.PHASE_STARTED
    : AI_PROMO_JOIN_REASONS.LOBBY_FULL;
};

function LobbyPage({lobbyId, onLobbyClosed, onGameStart, onGoToCrystals, onOpenInventory}) {
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const gameStartedRef = useRef(false);
  // Отложенное действие одиночного нажатия по иконке фракции (см.
  // FACTION_ICON_DOUBLE_CLICK_MS) и сам отложенный голос: перед
  // подтверждением банов его нужно отправить сразу, не дожидаясь таймера
  const factionIconClickTimerRef = useRef(null);
  const pendingBanVoteClickRef = useRef(null);
  // Отложенный выбор своей плитки рейтингового лобби (тот же таймер двойного
  // нажатия): перед подтверждением выбора его нужно отправить сразу
  const pendingOwnTileSelectRef = useRef(null);
  // Когда последний раз переключали подтверждение банов и выбора фракции
  // (см. CONFIRM_TOGGLE_GUARD_MS)
  const lastBanConfirmToggleAtRef = useRef(0);
  const lastFactionConfirmToggleAtRef = useRef(0);
  const factionSelectFlashTimerRef = useRef(null);
  const auth = useSelector((state) => state.private.auth);
  const lobby = useSelector((state) => state.private.lobby);
  const serverTimeDelta = useSelector(selectServerTimeDelta);
  // Десктоп — отдельная раскладка лобби (LobbyDesktop) с встроенным выбором
  // фракции; логика и обработчики у обеих общие, здесь
  const isDesktopLayout = useDesktopLayout();

  const [screen, setScreen] = useState(SCREENS_LOBBY.MAIN);
  const [timerEnabled, setTimerEnabled] = useState(true);
  const [fastMode, setFastMode] = useState(false);
  const [fieldPlayersMismatchMessage, setFieldPlayersMismatchMessage] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  // Кого создатель собирается исключить — до подтверждения в модалке
  const [kickConfirmPlayerId, setKickConfirmPlayerId] = useState(null);
  // Остаток отсчета — вместе с дедлайном, к которому он посчитан (см.
  // countdownSeconds ниже)
  const [countdownTick, setCountdownTick] = useState({deadlineAt: null, seconds: null});

  // Модалка с правилами текущей фазы — тот же текст, что в подсказке по
  // наведению: на тачскрине до него иначе не добраться
  const [isPhaseHintOpen, setIsPhaseHintOpen] = useState(false);

  // Карточка фракции, открытая двойным нажатием по иконке в плашке игрока
  // (на десктопе — и из встроенного выбора): помимо самой фракции и скина
  // помним, чья это была иконка, и isSelectable — от этого зависит, можно ли
  // выбрать фракцию прямо из карточки
  const [factionCard, setFactionCard] = useState(null);

  // Голоса фазы банов открыты всем участникам лобби и приезжают в дескрипторе
  // (lobby.factionBan, см. utils/lobby/factionBans.js). Здесь — только свои
  // голоса и подтверждение «в пути»: они показаны сразу, до эха сервера (см.
  // OPTIMISTIC_BAN_STATE_TTL_MS). Итог фазы приезжает уже в players
  const [pendingBanVotes, setPendingBanVotes] = useState({});
  const [pendingBanConfirmation, setPendingBanConfirmation] = useState(null);
  // То же для подтверждения выбора фракции в окне выбора рейтингового лобби:
  // готовность к старту сервер присылает в lobby.factionSelectReadyIds
  const [pendingFactionConfirmation, setPendingFactionConfirmation] = useState(null);

  // Только что выбранная во встроенном выборе десктопа фракция — на ее арте
  // секунду горит эффект «выбрано» ({factionId, at})
  const [factionSelectFlash, setFactionSelectFlash] = useState(null);

  // Плашка «игроки ищут лобби» для создателя-одиночки: количество ищущих и
  // среднее время присоединения оба «живут» на одних и тех же тиках — см. эффект ниже
  const [searchingCount, setSearchingCount] = useState(() => 3 + Math.floor(Math.random() * 15));
  const [searchingAvgSeconds, setSearchingAvgSeconds] = useState(() => 30 + Math.floor(Math.random() * 31));

  // Под каким id и именем клиент в лобби: гость входит только зрителем
  // начатого матча, под id вкладки (см. utils/lobby/guestViewer). Проверка
  // Boolean — у матч-мейкинговых лобби создателя нет (creatorId null)
  const {id: networkId, name: networkName, isGuest: isGuestViewer} = getLobbyNetworkIdentity(auth);
  const isCreator = Boolean(networkId) && networkId === lobby.creatorId;
  const isSuperAdmin = Boolean(auth.isSuperAdmin);
  const isAdminOrSuperAdmin = Boolean(auth.isAdmin) || isSuperAdmin;
  // ИИ-лобби собирает живых игроков по расписанию — фейковая плашка «игроки
  // ищут лобби» там была бы неправдой рядом с настоящим отсчетом
  const isAiLobby = Boolean(lobby.isAiMatch);
  const isSearchingHintVisible = (lobby.players || []).length === 1 && !isAiLobby;
  // Зритель ИИ-лобби до старта: мест не осталось, сервер пустил смотреть
  const isAiLobbySpectator = checkIfAiLobbySpectator(lobby, networkId);

  // Фаза банов идет, пока сервер присылает ее дедлайн (см. configs/factionBans.js).
  // Конец фазы объявляет только сервер: досрочно — когда подтвердили все
  // голосующие (кто из игроков неявный бот, клиент не знает), иначе по таймеру
  const isFactionBanPhase = Boolean(lobby.factionBanDeadlineAt);

  // Окно выбора фракции рейтингового лобби — после банов, до автостарта:
  // нажатие на фракцию только сообщает выбор серверу, а решение фиксирует
  // «Подтвердить выбор» (см. utils/lobby/rankFactionConfirm)
  const isRankFactionSelectWindow = Boolean(lobby.isRanked) && Boolean(lobby.rankMatchStartAt) && !isFactionBanPhase;

  // Рейтинговое лобби отсчитывает по очереди две метки времени сервера:
  // сначала дедлайн фазы банов, потом автостарт матча. Часы на оба отсчета
  // одни — меняется только подпись под ними
  const countdownDeadlineAt = lobby.factionBanDeadlineAt || lobby.rankMatchStartAt || null;

  useEffect(() => {
    if (!countdownDeadlineAt) {
      setCountdownTick({deadlineAt: null, seconds: null});
      return;
    }
    const updateCountdown = () => {
      setCountdownTick({
        deadlineAt: countdownDeadlineAt,
        seconds: getCountdownSecondsLeft(countdownDeadlineAt, serverTimeDelta),
      });
    };
    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, [countdownDeadlineAt, serverTimeDelta]);

  // Остаток — только от СВОЕГО дедлайна. Сменился дедлайн (баны кончились, и
  // пошел отсчет выбора фракции), а эффект выше еще не тикнул — в этом кадре
  // остаток считается заново, а не берется от прежнего отсчета: иначе «1 сек»
  // банов приняли бы за последние секунды выбора, и звук таймера сыграл бы
  // второй раз, уже поверх начала выбора
  const countdownSeconds = (() => {
    if (!countdownDeadlineAt) return null;
    if (countdownTick.deadlineAt === countdownDeadlineAt) return countdownTick.seconds;
    return getCountdownSecondsLeft(countdownDeadlineAt, serverTimeDelta);
  })();

  // ИИ-лобби до фазы банов: «Сбор игроков» до 21:00 или «Старт через» по
  // расписанию сервера (см. getAiLobbyCountdownState). Часы тикают, только
  // пока открыто ИИ-лобби
  const [aiCountdownClientNow, setAiCountdownClientNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isAiLobby) return undefined;
    setAiCountdownClientNow(Date.now());
    const intervalId = setInterval(() => setAiCountdownClientNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [isAiLobby]);
  const aiCountdownState = getAiLobbyCountdownState({lobby, now: aiCountdownClientNow + serverTimeDelta});

  // Вошел в ИИ-лобби зрителем — объясняем, почему нет выбора фракции.
  // Причину запоминаем на момент входа: дальше состав и фаза меняются, а
  // текст открытой модалки прыгать не должен. null — модалка закрыта
  const [aiSpectatorNoticeReason, setAiSpectatorNoticeReason] = useState(null);
  useEffect(() => {
    if (checkIfAiLobbySpectatorNoticeDue({
      lobby, userId: networkId, shownLobbyIds: shownAiSpectatorNoticeLobbyIds,
    })) {
      shownAiSpectatorNoticeLobbyIds.add(lobby.lobbyId);
      setAiSpectatorNoticeReason(getAiLobbySpectatorReason(lobby));
    }
  }, [lobby, networkId]);

  // Отложенный голос за бан не должен пережить экран: сокет к этому моменту
  // уже закрыт, а setState на размонтированном компоненте — течь. То же — у
  // эффекта «выбрано» десктопа
  useEffect(() => () => {
    clearTimeout(factionIconClickTimerRef.current);
    clearTimeout(factionSelectFlashTimerRef.current);
  }, []);

  // Свои голоса «в пути» снимаются, как только сервер прислал то же значение,
  // а нет — по истечении срока (см. pruneOptimisticBanVotes)
  useEffect(() => {
    const serverOwnVotes = lobby.factionBan?.votesByVoterId?.[networkId] || {};
    const prunedVotes = pruneOptimisticBanVotes(pendingBanVotes, serverOwnVotes, Date.now());
    if (prunedVotes !== pendingBanVotes) {
      setPendingBanVotes(prunedVotes);
      return undefined;
    }
    const sentAts = Object.values(pendingBanVotes).map((vote) => vote.sentAt);
    if (sentAts.length === 0) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setPendingBanVotes((prevVotes) => pruneOptimisticBanVotes(prevVotes, serverOwnVotes, Date.now()));
    }, Math.max(0, Math.min(...sentAts) + OPTIMISTIC_BAN_STATE_TTL_MS - Date.now()));
    return () => clearTimeout(timeoutId);
  }, [pendingBanVotes, lobby.factionBan, networkId]);

  // То же для подтверждения банов
  useEffect(() => {
    if (!pendingBanConfirmation) {
      return undefined;
    }
    const isServerConfirmed = (lobby.factionBan?.confirmedVoterIds || []).includes(networkId);
    const remainingMs = getOptimisticConfirmationRemainingMs(pendingBanConfirmation, isServerConfirmed, Date.now());
    if (remainingMs <= 0) {
      setPendingBanConfirmation(null);
      return undefined;
    }
    const timeoutId = setTimeout(() => setPendingBanConfirmation(null), remainingMs);
    return () => clearTimeout(timeoutId);
  }, [pendingBanConfirmation, lobby.factionBan, networkId]);

  // И для подтверждения выбора фракции: свое подтверждение клиент видит по
  // готовности (живой готов, только выбрав и подтвердив)
  useEffect(() => {
    if (!pendingFactionConfirmation) {
      return undefined;
    }
    const isServerConfirmed = (lobby.factionSelectReadyIds || []).includes(networkId);
    const remainingMs = getOptimisticConfirmationRemainingMs(pendingFactionConfirmation, isServerConfirmed, Date.now());
    if (remainingMs <= 0) {
      setPendingFactionConfirmation(null);
      return undefined;
    }
    const timeoutId = setTimeout(() => setPendingFactionConfirmation(null), remainingMs);
    return () => clearTimeout(timeoutId);
  }, [pendingFactionConfirmation, lobby.factionSelectReadyIds, networkId]);

  // Окно выбора закрылось (матч стартовал, лобби сменилось) — подтверждения
  // «в пути» больше нет
  useEffect(() => {
    if (!isRankFactionSelectWindow) {
      setPendingFactionConfirmation(null);
    }
  }, [isRankFactionSelectWindow]);

  // Фаза кончилась — голосов «в пути» больше нет, а отложенный клик-голос
  // (таймер двойного нажатия) не должен уйти уже после нее
  useEffect(() => {
    if (isFactionBanPhase) {
      return;
    }
    if (pendingBanVoteClickRef.current) {
      clearTimeout(factionIconClickTimerRef.current);
      factionIconClickTimerRef.current = null;
      pendingBanVoteClickRef.current = null;
    }
    setPendingBanVotes((prevVotes) => (Object.keys(prevVotes).length > 0 ? {} : prevVotes));
    setPendingBanConfirmation(null);
  }, [isFactionBanPhase]);

  // Раз в 3-8 секунд, пока плашка видна: количество ищущих меняется на
  // случайные ±1..5 (в пределах 5-25), среднее время присоединения — на
  // случайные ±1..10 секунд (в пределах 30-60)
  useEffect(() => {
    if (!isSearchingHintVisible) return;

    let timeoutId;
    const scheduleNextChange = () => {
      timeoutId = setTimeout(() => {
        setSearchingCount((prevCount) => {
          const delta = (1 + Math.floor(Math.random() * 5)) * (Math.random() < 0.5 ? -1 : 1);
          return Math.min(25, Math.max(5, prevCount + delta));
        });
        setSearchingAvgSeconds((prevSeconds) => {
          const delta = (1 + Math.floor(Math.random() * 10)) * (Math.random() < 0.5 ? -1 : 1);
          return Math.min(60, Math.max(30, prevSeconds + delta));
        });
        scheduleNextChange();
      }, 3000 + Math.floor(Math.random() * 5001));
    };
    scheduleNextChange();

    return () => clearTimeout(timeoutId);
  }, [isSearchingHintVisible]);

  usePageAnalytics('LobbyPage');

  useEffect(() => {
    MENU_SOUND_MANAGER.preloadFactionSounds();

    let ws;
    let closed = false;
    let reconnectAttempts = 0;
    let reconnectTimeoutId = null;

    const connect = async () => {
      try {
        // С кукой входа: ИИ-лобби сверяет присланный playerId с настоящим
        // входом (JWT-кука), прочие лобби ее просто не читают
        const joinRes = await fetch(`${API_BASE}/lobbies/${lobbyId}/join`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify({playerName: networkName, playerId: networkId}),
        });
        // Гостя сервер пускает только зрителем идущего матча. Определенный
        // отказ (матч закончился или завершается, лобби нет — в том числе после
        // перезапуска сервера посреди просмотра) — сокет не открываем: иначе
        // вместо понятного сообщения гость увидел бы «соединение разорвано».
        // Прочие ошибки (кратковременный 5xx) идут обычным путем переподключения
        if (isGuestViewer && checkIfGuestJoinRefused(joinRes.status) && !closed) {
          closed = true;
          // Отказ мог прийти на переподключении посреди просмотра: снимаем
          // оверлей переподключения и уходим в список с чистым состоянием
          triggerReconnectStatus(null);
          if (gameStartedRef.current) {
            closeNetworkSocket();
            dispatch(leaveLobby());
            dispatch(resetState());
          }
          dispatch(showToast(GUEST_SPECTATE_UNAVAILABLE_MESSAGE, 'warning', 4000, true));
          onLobbyClosed();
          return;
        }
      } catch (e) {
        console.error('[LobbyPage] Join error', e);
      }
      if (closed) return;
      // heartbeat=1 — клиент отвечает на пульс сервера (PONG, см. onmessage):
      // сервер вправе оборвать молчащий сокет, даже если тот умер до первого
      // ответа (см. resolveSocketHeartbeatSupport)
      const wsUrl = `${WS_BASE}?lobbyId=${encodeURIComponent(lobbyId)}&playerId=${encodeURIComponent(networkId)}&heartbeat=1`;
      ws = new WebSocket(wsUrl);

      // Свой сторож тишины на каждое соединение: снимается его же onclose
      let lastServerMessageAt = Date.now();
      let isSilenceHintShown = false;
      const silenceIntervalId = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const silenceMs = Date.now() - lastServerMessageAt;

        if (silenceMs < SERVER_SILENCE_LIMIT_MS) {
          if (silenceMs >= SERVER_SILENCE_HINT_MS && !isSilenceHintShown) {
            isSilenceHintShown = true;
            console.warn(`[LobbyPage] no data from server for ${silenceMs}ms`);
            sendNetworkEvent('server_silence_hint');
            triggerReconnectStatus({isWaitingForServer: true});
          }
          return;
        }

        console.warn(`[LobbyPage] no data from server for ${silenceMs}ms — reconnecting`);
        sendNetworkEvent('silence_reconnect');
        // Сокет брошен нами: сервер может успеть прислать в него SESSION_SUPERSEDED
        // (наше же переподключение выглядит для него входом со второго
        // устройства) — такие сообщения игрок видеть не должен
        ws.isAbandonedByWatchdog = true;
        // Флаг «закрытие ожидаемо» глобальный на все сокеты приложения и мог
        // остаться взведенным с другого экрана — тогда onclose ниже молча
        // проглотил бы и переподключение, и модалку обрыва
        clearExpectingClose();
        ws.close();
      }, SILENCE_CHECK_INTERVAL_MS);

      ws.onopen = () => {
        console.log('[LobbyPage] WS connected');
        setNetworkSocket(ws);
        clearExpectingClose();
        lastServerMessageAt = Date.now();
        // reconnectAttempts > 0 здесь означает, что это не первое подключение,
        // а восстановление после обрыва в матче — свежий LOAD_STATE ниже еще
        // потребует какое-то время на перерисовку поля (см. GameContent)
        if (gameStartedRef.current && reconnectAttempts > 0) {
          triggerMidGameReconnected();
        }
        reconnectAttempts = 0;
        triggerReconnectStatus(null);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (ws.isAbandonedByWatchdog) return;
          lastServerMessageAt = Date.now();
          if (isSilenceHintShown) {
            // Связь ожила сама (короткая просадка сети, спящий телефон) —
            // снимаем плашку, не доводя до переподключения
            isSilenceHintShown = false;
            triggerReconnectStatus(null);
          }

          // Слушатели трафика (конец матча в GameContent, делегирование ботов
          // в OnlineBotRunner) получают копию сообщения — раньше они ради
          // этого подменяли ws.onmessage друг поверх друга. Вместе с сокетом,
          // по которому оно пришло: работа бота привязана к соединению
          notifyNetworkMessageSubscribers(msg, ws);

          if (msg.type === 'PING') {
            // Пульс сервера: отвечаем на том же сокете, с которого пришли
            try {
              ws.send(JSON.stringify({type: 'PONG'}));
            } catch (e) {
              console.error('[LobbyPage] PONG send failed', e);
            }
            return;
          }
          if (msg.type === 'LOBBY_NOT_FOUND') {
            // Лобби на сервере больше нет, хотя сокет открыт: экшены сюда уже
            // никогда не придут и переподключение не поможет — честно
            // показываем обрыв вместо молчаливого зависания
            console.error('[LobbyPage] lobby is gone on the server');
            sendNetworkEvent('lobby_not_found');
            clearExpectingClose();
            triggerConnectionLost();
            return;
          }
          if (msg.type === 'LOBBY_UPDATE') {
            dispatch({...updateLobby(msg.payload), _fromServer: true});
          } else if (msg.type === 'LOAD_STATE') {
            dispatch({...loadState(msg.payload.loadedState, msg.payload.finalRound), _fromServer: true});
            gameStartedRef.current = true;
            onGameStart();
          } else if (msg.type === 'LOBBY_CLOSED') {
            // Сервер отменил матч дня против ИИ (никто не пришел к отсечке и т.п.)
            if (msg.payload?.reason === AI_PROMO_CANCELLED_REASON) {
              dispatch(showToast(TEXT_AI_MATCH_CANCELLED_TODAY, 'info', 4000, true));
            }
            dispatch({...leaveLobby(), _fromServer: true});
            closeNetworkSocket();
            onLobbyClosed();
          } else if (msg.type === 'AUTH_REQUIRED') {
            // ИИ-лобби не признало вход (истек, вышли в другой вкладке) —
            // сервер закрывает сокет следом (код 4005). Переподключение не
            // поможет: уходим в список с чистым состоянием и просим войти
            closed = true;
            triggerReconnectStatus(null);
            markExpectingClose();
            closeNetworkSocket();
            dispatch({...leaveLobby(), _fromServer: true});
            if (gameStartedRef.current) {
              dispatch(resetState());
            }
            dispatch(showToast(TEXT_AI_LOBBY_AUTH_REQUIRED, 'warning', 4000, true));
            onLobbyClosed();
          } else if (msg.type === 'KICKED') {
            dispatch({...leaveLobby(), _fromServer: true});
            closeNetworkSocket();
            onLobbyClosed();
          } else if (msg.type === 'SESSION_SUPERSEDED') {
            // Тот же игрок зашел в этот матч с другого устройства/вкладки —
            // сервер уже закрывает это соединение следом за сообщением.
            // Экран намеренно не меняем (leaveLobby/onLobbyClosed не зовем) —
            // оверлей в App просто перекрывает текущий, пока не перезагрузят
            closeNetworkSocket();
            triggerSessionSuperseded();
          } else if (msg.type === 'GAME_ENDED') {
            closeNetworkSocket();
          } else if (msg.type === 'STALE_ACTION_REJECTED') {
            // Сервер отверг действие, отправленное из уже неактуального
            // контекста хода (например, стройку, нажатую в момент завершения
            // хода серверным таймером): следом придет свежий LOAD_STATE.
            // Включаем тот же оверлей с перерисовкой поля, что и при
            // восстановлении сокета посреди матча, — локально примененное
            // действие сотрется актуальным состоянием
            console.warn('[LobbyPage] stale action rejected by server', msg.payload?.actionType);
            // Каждый такой отказ — оверлей у игрока. Что закрыло ход и
            // насколько действие опоздало, сервер прислал вместе с отказом
            sendNetworkEvent('stale_action_rejected', getStaleActionRejectedEventValue(msg.payload));
            triggerMidGameReconnected();
          } else if (msg.type === 'ERROR') {
            const errorMessage = typeof msg.payload === 'string' ? msg.payload : 'Ошибка лобби';
            dispatch(showToast(errorMessage, 'error', 4000, true));
          } else if (msg.type === 'FACTION_BANS_RESOLVED') {
            // Отдельное сообщение только ради звука: сам итог приедет обычным
            // LOBBY_UPDATE, которых до старта матча приходит много — звук
            // переигрывался бы на каждом
            MENU_SOUND_MANAGER.playFactionSelectIntro();
          } else if (msg.type === 'FIELD_PLAYERS_MISMATCH_ERROR') {
            // Отдельно от общего ERROR/тоста — админ должен явно закрыть
            // модалку и осознанно поправить выбор поля или число игроков
            setFieldPlayersMismatchMessage(msg.payload?.message || 'Количество игроков в лобби не соответствует выбранному полю');
          } else if (msg.type === 'SET_EXTENDED_GAME_RESULTS') {
            // Награды и закрытые задания — в локальный профиль сразу, без
            // перезапроса /auth/me (см. getOwnMatchResultProfileActions)
            const ownResult = msg.payload?.find((r) => r.id === auth.id);
            // Рейтинговость — из состояния партии: она приходит с LOAD_STATE
            // и не устаревает в замыкании обработчика, в отличие от lobby
            const isRanked = Boolean(selectIsRankedMatch(store.getState()));
            getOwnMatchResultProfileActions(ownResult, {isRanked}).forEach((action) => dispatch(action));
            dispatch({...msg, _fromServer: true});
          } else if (applyMatchChatServerMessage(msg, dispatch)) {
            // Модерируемый чат ИИ-матча: свои сообщения на проверке, отказы и
            // пауза чата уходят в приватный слайс matchChat. Сырые сообщения
            // сервера в общий стор не попадают; одобренное сообщение придет
            // обычным ADD_CHAT_MESSAGE по общей ветке ниже
          } else {
            // Generic game action relayed by server — применяется только если
            // тип в белом списке (см. applyRelayedServerAction)
            applyRelayedServerAction(msg, store, dispatch);
            // console.log('TO CLIENT');
            // console.log({...msg, _fromServer: true})
          }
        } catch (e) {
          console.error('[LobbyPage] WS message parse error', e);
        }
      };

      ws.onclose = () => {
        console.log('[LobbyPage] WS disconnected');
        clearInterval(silenceIntervalId);
        if (isExpectingClose()) {
          return;
        }

        // gameStartedRef остается true и после того, как LobbyPage
        // размонтировался (экран переключился на GAME) — этот эффект и его
        // замыкания (включая сам connect) продолжают жить, пока сокет жив
        if (gameStartedRef.current && reconnectAttempts < MID_GAME_RECONNECT_DELAYS_MS.length) {
          const delay = MID_GAME_RECONNECT_DELAYS_MS[reconnectAttempts];
          reconnectAttempts += 1;
          console.log(`[LobbyPage] Reconnect attempt ${reconnectAttempts}/${MID_GAME_RECONNECT_DELAYS_MS.length} in ${delay}ms`);
          triggerReconnectStatus({
            attempt: reconnectAttempts,
            maxAttempts: MID_GAME_RECONNECT_DELAYS_MS.length,
            retryInMs: delay
          });
          reconnectTimeoutId = setTimeout(() => {
            reconnectTimeoutId = null;
            // closed/isExpectingClose могли стать true, пока таймер ждал
            // (например, игрок вышел в главное меню) — тогда переподключаться не нужно
            if (closed || isExpectingClose()) {
              triggerReconnectStatus(null);
              return;
            }
            connect();
          }, delay);
          return;
        }

        triggerReconnectStatus(null);
        triggerConnectionLost();
      };

      ws.onerror = (err) => {
        console.error('[LobbyPage] WS error', err);
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      // Если матч уже начался, LobbyPage размонтируется (экран уходит на
      // GAME), но сокет и это замыкание должны продолжать жить и
      // самостоятельно переподключаться при обрыве — поэтому closed не
      // взводим и запланированную попытку реконнекта не отменяем
      if (!gameStartedRef.current) {
        closed = true;
        if (reconnectTimeoutId) {
          clearTimeout(reconnectTimeoutId);
          reconnectTimeoutId = null;
        }
      }
      if (ws) {
        if (!gameStartedRef.current && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
          ws.onmessage = null;
          ws.onclose = null;
          ws.onerror = null;
          markExpectingClose();
          ws.close();
          wsRef.current = null;
        }
      }
    };
  }, [lobbyId, networkId, networkName, isGuestViewer, auth.id, dispatch, onLobbyClosed, onGameStart, setFieldPlayersMismatchMessage]);

  const players = lobby.players || [];
  const actingPlayer = players.find(player => player.id === networkId);
  const playerNamesById = new Map(players.map((player) => [player.id, player.name]));

  // ===== Фаза банов: голоса, подтверждения, кто голосует =====
  // Пока фаза идет, сервер кладет в дескриптор участников голоса всех и
  // подтверждения (lobby.factionBan). Свои голоса и подтверждение «в пути»
  // накладываются поверх — до эха сервера
  const factionBan = isFactionBanPhase ? lobby.factionBan : null;
  const serverBanVotesByVoterId = factionBan?.votesByVoterId || {};
  const ownBanVotes = {...(serverBanVotesByVoterId[networkId] || {})};
  Object.entries(pendingBanVotes).forEach(([targetPlayerId, vote]) => {
    ownBanVotes[targetPlayerId] = vote.factionId;
  });
  // Кто голосует, решает сервер (по текущему составу); без списка (старый
  // сервер) — по тем же правилам здесь: боты со значком и ИИ не банят
  const banVoterIds = factionBan?.voterIds || getFactionBanVoterIds(players);
  const banConfirmedVoterIds = new Set(factionBan?.confirmedVoterIds || []);
  if (pendingBanConfirmation && networkId) {
    if (pendingBanConfirmation.isConfirmed) {
      banConfirmedVoterIds.add(networkId);
    } else {
      banConfirmedVoterIds.delete(networkId);
    }
  }
  // Зритель ИИ-лобби (вошел, когда баны уже шли) не голосует: сервер его
  // голос отбросит, а оптимистичная метка осталась бы
  const isBanVoter = isFactionBanPhase && !isAiLobbySpectator && Boolean(networkId) && banVoterIds.includes(networkId);
  const isOwnBanConfirmed = isBanVoter && banConfirmedVoterIds.has(networkId);
  // На нуле отсчета подтверждать (и голосовать) поздно: фазу вот-вот закроет
  // таймер сервера
  const isBanConfirmDisabled = countdownSeconds === 0;
  // Подтвердивший свои голоса больше не меняет — до «Изменить баны»
  const isOwnBanVoteAllowed = isBanVoter && !isOwnBanConfirmed && !isBanConfirmDisabled;
  const banVotesByVoterId = isBanVoter
    ? {...serverBanVotesByVoterId, [networkId]: ownBanVotes}
    : serverBanVotesByVoterId;
  const banConfirmedCount = banVoterIds.filter((voterId) => banConfirmedVoterIds.has(voterId)).length;
  // Кому из соперников голос еще не отдан — подсказка у кнопки подтверждения
  const missingBanTargetNames = isBanVoter
    ? getMissingBanTargetIds({voterId: networkId, players, votes: ownBanVotes})
      .map((playerId) => playerNamesById.get(playerId))
      .filter(Boolean)
    : [];

  // Метка игрока на время фазы: подтвердил, еще выбирает или не банит вовсе
  const getPlayerBanStatus = (player) => {
    if (!isFactionBanPhase) return null;
    if (!banVoterIds.includes(player.id)) return LOBBY_BAN_STATUSES.NOT_VOTING;
    return banConfirmedVoterIds.has(player.id) ? LOBBY_BAN_STATUSES.CONFIRMED : LOBBY_BAN_STATUSES.CHOOSING;
  };

  // Кто проголосовал за бан фракции цели — для счетчика и чипов с никами
  const getFactionBanVoters = (targetPlayerId, factionId) => {
    if (!isFactionBanPhase) return [];
    return getBanVoterIdsForFaction({votesByVoterId: banVotesByVoterId, targetPlayerId, factionId})
      .filter((voterId) => playerNamesById.has(voterId))
      .map((voterId) => ({
        id: voterId,
        name: voterId === networkId ? 'Вы' : playerNamesById.get(voterId),
        isOwn: voterId === networkId,
      }));
  };

  // ===== Окно выбора фракции: подтверждение выбора и готовность =====
  // Кто готов к старту, решает сервер (factionSelectReadyIds: живой — выбрал
  // и подтвердил, бот и ИИ — выбрали). Свое подтверждение «в пути»
  // накладывается поверх — до эха сервера, как у банов
  const serverFactionSelectReadyIds = isRankFactionSelectWindow ? (lobby.factionSelectReadyIds || []) : [];
  // Подтверждает выбор только живой игрок лобби, не зритель ИИ-лобби
  const isFactionConfirmer = isRankFactionSelectWindow && !isAiLobbySpectator
    && checkIfFactionConfirmRequired(actingPlayer);
  const factionSelectReadyIds = new Set(serverFactionSelectReadyIds);
  if (isFactionConfirmer && pendingFactionConfirmation) {
    if (pendingFactionConfirmation.isConfirmed) {
      factionSelectReadyIds.add(networkId);
    } else {
      factionSelectReadyIds.delete(networkId);
    }
  }
  const isOwnFactionConfirmed = isFactionConfirmer && factionSelectReadyIds.has(networkId);
  const hasOwnFaction = Boolean(actingPlayer?.factionId);
  // Подтверждать нечего, пока фракция не выбрана, и поздно на нуле отсчета:
  // матч вот-вот запустит таймер сервера (и выбранной фракцией)
  const isFactionConfirmDisabled = countdownSeconds === 0 || (!isOwnFactionConfirmed && !hasOwnFaction);
  const factionSelectReadyCount = players.filter((player) => factionSelectReadyIds.has(player.id)).length;

  // Метка «Готов». В окне выбора рейтингового лобби — готов к старту (см.
  // factionSelectReadyIds), остальные, и соперники, и сам игрок, — «Выбирает
  // фракцию»: выбор без подтверждения еще не решение. Вне окна — выбрал
  // фракцию
  const checkIfPlayerReady = (player) => (isRankFactionSelectWindow
    ? factionSelectReadyIds.has(player.id)
    : Boolean(player.factionId));

  // Ботов в рейтинговое лобби сервер добавляет только до начала подготовки
  // (баны и выбор фракции): состав к этому моменту уже зафиксирован
  const isRankedPreparationStarted = checkIfRankedPhaseStarted(lobby)
    || (Boolean(lobby.isRanked) && Boolean(lobby.rankMatchStartAt));

  const handleLeave = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'LEAVE_LOBBY'}));
    }
    closeNetworkSocket();
    dispatch(leaveLobby());
    onLobbyClosed();
  };

  const handleStartGame = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'START_GAME',
        payload: {timerEnabled: lobby.isRanked ? true : timerEnabled, fastMode}
      }));

      lobby.players.forEach((player) => {
        sendButtonClickEvent('LobbyPage', 'start_game_faction', player.factionId);
      });
    }
  };

  // Только для создателя обычного лобби: бонус на всех игроков лобби (см.
  // utils/lobby/lobbyBonus). Уровень меняется на сервере, и участники, включая
  // самого создателя, получают его с LOBBY_UPDATE
  const handleSelectLobbyBonus = (bonusType) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      MENU_SOUND_MANAGER.playMenuButtonClick();
      ws.send(JSON.stringify({type: 'SET_LOBBY_BONUS', payload: {bonusType}}));
    }
  };

  // Голос за бан фракции другому игроку. Голос один на цель: повторный клик по
  // той же фракции его снимает (серверу уходит factionId: null), клик по другой
  // фракции той же цели — переносит. Локально применяем сразу, не дожидаясь
  // эха: сервер пришлет голоса всех в дескрипторе (см. pendingBanVotes)
  const handleBanFaction = (targetPlayerId, factionId) => {
    const nextFactionId = ownBanVotes[targetPlayerId] === factionId ? null : factionId;
    MENU_SOUND_MANAGER.playMenuButtonClick();
    const serverFactionIdAtSend = (serverBanVotesByVoterId[networkId] || {})[targetPlayerId] || null;
    setPendingBanVotes((prevVotes) => ({
      ...prevVotes,
      [targetPlayerId]: {factionId: nextFactionId, sentAt: Date.now(), serverFactionIdAtSend},
    }));

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'BAN_FACTION', payload: {targetPlayerId, factionId: nextFactionId}}));
    }
  };

  // Голос за бан ждет, не окажется ли нажатие половиной двойного (см.
  // FACTION_ICON_DOUBLE_CLICK_MS): в фазе банов карточку открывает именно
  // двойное. Звук и оптимистичный значок голоса откладываются вместе с
  // отправкой — иначе двойное успевало бы щелкнуть и мигнуть значком
  const handleBanVoteClick = (targetPlayerId, factionId) => {
    if (!isOwnBanVoteAllowed) return;
    clearTimeout(factionIconClickTimerRef.current);
    pendingBanVoteClickRef.current = {targetPlayerId, factionId};
    factionIconClickTimerRef.current = setTimeout(() => {
      factionIconClickTimerRef.current = null;
      pendingBanVoteClickRef.current = null;
      handleBanFaction(targetPlayerId, factionId);
    }, FACTION_ICON_DOUBLE_CLICK_MS);
  };

  // Подтверждение своих банов (isConfirmed: true) или его снятие ради
  // переголосования («Изменить баны»). Голос, еще ждущий таймера двойного
  // нажатия, уходит сразу и раньше подтверждения: после него сервер голоса
  // уже не принимает
  const handleConfirmFactionBans = (isConfirmed) => {
    if (Date.now() - lastBanConfirmToggleAtRef.current < CONFIRM_TOGGLE_GUARD_MS) return;
    lastBanConfirmToggleAtRef.current = Date.now();
    if (isConfirmed && factionIconClickTimerRef.current) {
      clearTimeout(factionIconClickTimerRef.current);
      factionIconClickTimerRef.current = null;
      const pendingClick = pendingBanVoteClickRef.current;
      pendingBanVoteClickRef.current = null;
      if (pendingClick) {
        handleBanFaction(pendingClick.targetPlayerId, pendingClick.factionId);
      }
    }
    setPendingBanConfirmation({
      isConfirmed,
      sentAt: Date.now(),
      serverIsConfirmedAtSend: (factionBan?.confirmedVoterIds || []).includes(networkId),
    });

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'CONFIRM_FACTION_BANS', payload: {isConfirmed}}));
    }
  };

  const handleOpenPhaseHint = () => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setIsPhaseHintOpen(true);
  };

  const handleClosePhaseHint = () => {
    MENU_SOUND_MANAGER.playSideMenuClick();
    setIsPhaseHintOpen(false);
  };

  const openFactionCard = (player, factionId) => {
    // Сюда приходит и двойное нажатие в фазе банов (и по своей плитке в окне
    // выбора) — отложенный голос или выбор снимаем
    clearTimeout(factionIconClickTimerRef.current);
    factionIconClickTimerRef.current = null;
    pendingBanVoteClickRef.current = null;
    pendingOwnTileSelectRef.current = null;
    MENU_SOUND_MANAGER.playSideMenuClick();
    // Скин — того игрока, по чьей иконке нажали: одна и та же фракция у
    // соперника может выглядеть иначе, чем у тебя. В обычном лобби набора
    // скинов нет — там только скин выбранной фракции
    const skinId = player.selectedSkins?.[factionId]
      || (factionId === player.factionId ? player.selectedSkin : undefined);
    setFactionCard({playerId: player.id, factionId, skinId: skinId || undefined});
  };

  // Карточка своей фракции из встроенного выбора десктопа: скин — из своей
  // раскладки. isSelectable — можно ли выбрать фракцию прямо из карточки
  // (закрытую в обычном лобби — нельзя)
  const openOwnFactionCard = (factionId, skinId, isSelectable) => {
    MENU_SOUND_MANAGER.playSideMenuClick();
    setFactionCard({playerId: networkId, factionId, skinId, isSelectable});
  };

  const sendSelectFaction = (factionId) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'SELECT_FACTION', payload: {factionId}}));
    }
  };

  // Эффект «выбрано» на арте фракции десктопа (см. FACTION_SELECT_FLASH_MS)
  const flashFactionSelect = (factionId) => {
    clearTimeout(factionSelectFlashTimerRef.current);
    setFactionSelectFlash({factionId, at: Date.now()});
    factionSelectFlashTimerRef.current = setTimeout(() => {
      factionSelectFlashTimerRef.current = null;
      setFactionSelectFlash(null);
    }, FACTION_SELECT_FLASH_MS);
  };

  // Короткий путь мимо карусели: выбрать фракцию прямо из ее карточки. Звук
  // выбора — тот же, что в карусели, а вот ритуальную анимацию она играет сама
  const handleSelectFactionFromCard = (factionId) => {
    if (isOwnFactionConfirmed) return;
    MENU_SOUND_MANAGER.playFactionSelect(factionId);
    sendSelectFaction(factionId);
    flashFactionSelect(factionId);
    setFactionCard(null);
  };

  // Встроенный выбор фракции десктопа (карусель, сетка, плитки рейтингового
  // лобби): без отдельного экрана и полноэкранного ритуала — звук, выбор и
  // короткий эффект на арте. Повторное нажатие по уже выбранной (или только
  // что выбранной — эхо сервера еще в пути) фракции новым выбором не считается:
  // так и двойное нажатие по плитке, открывающее карточку, не выбирает дважды
  const handleSelectFactionInline = (factionId) => {
    // Подтвердивший выбор меняет его только через «Изменить выбор»
    if (isOwnFactionConfirmed) return;
    if (factionId === actingPlayer?.factionId || factionSelectFlash?.factionId === factionId) return;
    if (factionId === RANDOM_FACTION_ID) {
      // У моковой фракции нет собственного звука выбора — только общее интро
      MENU_SOUND_MANAGER.playFactionSelectIntro();
    } else {
      MENU_SOUND_MANAGER.playFactionSelect(factionId);
    }
    sendButtonClickEvent('LobbyPage', 'select_faction_inline', factionId);
    sendSelectFaction(factionId);
    flashFactionSelect(factionId);
  };

  // Нажатие по своей плитке рейтингового лобби — выбор, двойное — карточка
  // фракции. Выбор ждет, не окажется ли нажатие половиной двойного (тот же
  // таймер, что у голоса за бан; карточка его снимает): иначе, открывая
  // карточку, игрок заодно менял бы фракцию, а то и запускал матч
  const handleOwnTileSelectClick = (factionId) => {
    clearTimeout(factionIconClickTimerRef.current);
    pendingOwnTileSelectRef.current = factionId;
    factionIconClickTimerRef.current = setTimeout(() => {
      factionIconClickTimerRef.current = null;
      pendingOwnTileSelectRef.current = null;
      handleSelectFactionInline(factionId);
    }, FACTION_ICON_DOUBLE_CLICK_MS);
  };

  // Подтверждение выбора фракции (isConfirmed: true) или его снятие ради
  // смены фракции («Изменить выбор»). Выбор плитки, еще ждущий таймера
  // двойного нажатия, уходит сразу и раньше подтверждения: после него сервер
  // выбор уже не принимает
  const handleConfirmFactionSelection = (isConfirmed) => {
    if (Date.now() - lastFactionConfirmToggleAtRef.current < CONFIRM_TOGGLE_GUARD_MS) return;
    lastFactionConfirmToggleAtRef.current = Date.now();
    if (isConfirmed && factionIconClickTimerRef.current) {
      clearTimeout(factionIconClickTimerRef.current);
      factionIconClickTimerRef.current = null;
      const pendingFactionId = pendingOwnTileSelectRef.current;
      pendingOwnTileSelectRef.current = null;
      if (pendingFactionId) {
        handleSelectFactionInline(pendingFactionId);
      }
    }
    setPendingFactionConfirmation({
      isConfirmed,
      sentAt: Date.now(),
      serverIsConfirmedAtSend: serverFactionSelectReadyIds.includes(networkId),
    });

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'CONFIRM_FACTION_SELECTION', payload: {isConfirmed}}));
    }
  };

  // Крестик на плашке (карточке) соперника — только вопрос: исключает
  // подтверждение в модалке (промах по крестику выкидывал игрока сразу)
  const handleKickPlayer = (playerId) => {
    setKickConfirmPlayerId(playerId);
  };

  const handleConfirmKickPlayer = () => {
    const playerId = kickConfirmPlayerId;
    setKickConfirmPlayerId(null);
    const ws = wsRef.current;
    if (playerId && ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'KICK_PLAYER', payload: {playerId}}));
    }
  };

  // Игрок ушел сам (или создателем стал другой), пока модалка открыта, —
  // спрашивать уже не о ком
  const kickConfirmPlayer = kickConfirmPlayerId
    ? players.find((player) => player.id === kickConfirmPlayerId)
    : null;
  const isKickConfirmStale = Boolean(kickConfirmPlayerId) && (!kickConfirmPlayer || !isCreator);
  useEffect(() => {
    if (isKickConfirmStale) {
      setKickConfirmPlayerId(null);
    }
  }, [isKickConfirmStale]);

  // Только для суперадмина-создателя лобби: тестовое добавление случайного
  // свободного бота из пула, минуя обычную органическую логику присоединения
  const handleAddRandomBot = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'ADD_RANDOM_BOT'}));
    }
  };

  // Только для админа-создателя рейтингового лобби (создано через AdminPage):
  // добавляет свободного бота из пула, который затем сам выберет фракцию по
  // таймауту — как и боты, присоединяющиеся к обычным/матч-мейкинговым лобби
  const handleAddRankedBot = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'ADD_RANDOM_BOT_TO_RANKED_LOBBY'}));
    }
  };

  // Только для админа-создателя лобби (обычного и рейтингового): выбор
  // конкретного поля вместо случайного подбора при старте матча —
  // fieldId=null значит "Случайное поле" (прежнее поведение по умолчанию)
  const handleSelectField = (fieldId) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'SELECT_LOBBY_FIELD', payload: {fieldId: fieldId || null}}));
    }
    setScreen(SCREENS_LOBBY.MAIN);
  };

  // ===== Последние секунды отсчета: подсветка и звук таймера =====
  // Подсветка — у всех, звук — только тому, кому еще надо действовать: как в
  // матче таймер хода звучит лишь у того, чей ход. В фазе банов это
  // голосующий, не подтвердивший баны; в окне выбора — живой игрок, не
  // подтвердивший выбор (выбрать мало: решение — подтверждение)
  const countdownUrgency = getLobbyCountdownUrgency(countdownSeconds);
  const isCountdownActionPending = isFactionBanPhase
    ? isBanVoter && !isOwnBanConfirmed
    : isFactionConfirmer && !isOwnFactionConfirmed;
  const isCountdownSoundDue = countdownUrgency === LOBBY_COUNTDOWN_URGENCY.CRITICAL && isCountdownActionPending;

  // Звук — один раз на отсчет: ключ — дедлайн, по которому он идет
  const countdownSoundDeadlineRef = useRef(null);
  useEffect(() => {
    if (isCountdownSoundDue && countdownSoundDeadlineRef.current !== countdownDeadlineAt) {
      countdownSoundDeadlineRef.current = countdownDeadlineAt;
      MENU_SOUND_MANAGER.playLobbyCountdown();
    }
  }, [isCountdownSoundDue, countdownDeadlineAt]);

  // Успел (подтвердил баны или выбор фракции), фаза сменилась или экран ушел —
  // тиканье обрывается
  useEffect(() => {
    if (!isCountdownActionPending) {
      MENU_SOUND_MANAGER.stopLobbyCountdown();
    }
    return () => MENU_SOUND_MANAGER.stopLobbyCountdown();
  }, [isCountdownActionPending, countdownDeadlineAt]);

  // Высота содержимого полосы над списком игроков на телефоне (плашки
  // отсчета): по ней полоса плавно подстраивается, когда плашка появляется
  // или переносится на две строки (см. .lobby-countdown-slot_measured)
  const [countdownSlotContentRef, countdownSlotContentHeight] = useObservedHeight();

  // Картинки телефонного лобби — очередью (см. getLobbyPreloadPaths): иконки
  // фракций, за ними полные арты своих фракций, за ними — соперников, в фоне
  // и по две, чтобы не забивать канал. На десктопе полные арты и так на
  // экране (плитки выбора), там очередь не нужна
  const isLobbyPreloadEnabled = !isDesktopLayout && Boolean(lobby.lobbyId) && !isGuestViewer;
  const lobbyPreloadPaths = getLobbyPreloadPaths({
    players,
    ownPlayerId: networkId,
    factionLoadouts: auth.factionLoadouts,
  });
  const lobbyIconsPreload = useImagePreload(lobbyPreloadPaths.iconPaths, {isEnabled: isLobbyPreloadEnabled});
  // Свои арты держат показ выбранной фракции — им высокий приоритет
  const ownFactionArtsPreload = useImagePreload(lobbyPreloadPaths.ownArtPaths, {
    isEnabled: isLobbyPreloadEnabled && lobbyIconsPreload.isSettled,
    fetchPriority: 'high',
    retain: true,
  });
  // И после иконок тоже: у зрителя своих артов нет, и без этого чужие пошли
  // бы вперемешку с иконками
  useImagePreload(lobbyPreloadPaths.otherArtPaths, {
    isEnabled: isLobbyPreloadEnabled && lobbyIconsPreload.isSettled && ownFactionArtsPreload.isSettled,
    concurrency: 2,
    fetchPriority: 'low',
  });
  // Арт своей выбранной фракции (и место под него) — только когда загрузка
  // артов ВСЕХ своих фракций кончилась (упавшая не держит остальных): игрок
  // переключает выбор, и каждая картинка должна встать сразу, без пустого
  // места. И только загруженный: на экран не попадает картинка, которую
  // браузер еще ждет, — иначе <img> со сменой src показывал бы прежнюю
  // фракцию, пока новая не придет (а при зависшем запросе — всегда). Не
  // загрузился арт своей фракции — над списком пусто, как без выбора
  const ownFactionArtPath = hasOwnFaction
    ? getOwnLobbyFactionArtPath(actingPlayer, actingPlayer.factionId, auth.factionLoadouts)
    : null;
  const shownOwnFactionArtPath = ownFactionArtPath && ownFactionArtsPreload.isSettled
    && ownFactionArtsPreload.statuses[ownFactionArtPath] === IMAGE_LOAD_STATUS.LOADED
    ? ownFactionArtPath
    : null;

  // Выбор на экране выбора фракции (карусель): арт новой фракции начинает
  // грузиться сразу, пока идет ритуал выбора, — к ответу сервера он уже готов,
  // и место над списком не сжимается на смене. Нужно это обычному лобби (в
  // том числе для «Случайной»): своих артов там заранее не знают. В
  // рейтинговом свои, включая «Случайную», уже в очереди — здесь только
  // срочность и повтор упавшей: явный выбор — повод попробовать снова
  const handleFactionSelectStart = (factionId) => {
    loadImageOnce(getOwnLobbyFactionArtPath(actingPlayer, factionId, auth.factionLoadouts), {
      fetchPriority: 'high',
      isRetryAfterError: true,
    });
  };

  // Гость в лобби не участник, а зритель: экран сбора (фракции, приглашение,
  // старт) ему не нужен — до прихода состояния матча только загрузка
  if (!lobby.lobbyId || isGuestViewer) {
    return (
      <h1 className="lobby-page-title">Загрузка...</h1>
    );
  }

  const goToMainLobbyScreen = () => {
    setScreen(SCREENS_LOBBY.MAIN);
  }

  const handleSelectFaction = () => {
    setScreen(SCREENS_LOBBY.FACTION_SELECT);
  }

  const handleOpenFieldSelect = () => {
    setScreen(SCREENS_LOBBY.FIELD_SELECT);
  }

  if (screen === SCREENS_LOBBY.FACTION_SELECT) {
    return (
      <FactionSelectPage
        goToMainLobbyScreen={goToMainLobbyScreen}
        isRanked={lobby.isRanked}
        availableFactionIds={actingPlayer?.availableFactionIds || []}
        bannedFactionId={actingPlayer?.bannedFactionId}
        actingPlayerFactionId={actingPlayer?.factionId}
        // Подтвердившему выбор сменить фракцию можно только через «Изменить
        // выбор» — экран выбора тогда ничего серверу не шлет
        isSelectionLocked={isOwnFactionConfirmed}
        onFactionSelectStart={handleFactionSelectStart}
        onGoToCrystals={onGoToCrystals}
        onOpenInventory={onOpenInventory}
      />
    )
  }

  if (screen === SCREENS_LOBBY.FIELD_SELECT) {
    return (
      <LobbySelectFieldPage
        selectedFieldId={lobby.selectedFieldId}
        onSelectField={handleSelectField}
        onBack={goToMainLobbyScreen}
      />
    )
  }

  // В рейтинговом лобби «Начать матч» открывает не матч, а фазу банов (см.
  // startFactionBanPhase на сервере), и до нее фракций нет ни у кого — ждать
  // их выбора кнопке нечего. Дальше все идет само: после банов открывается
  // окно выбора фракции со своим авто-стартом. Пока фаза идет, кнопка
  // заблокирована — второй раз запускать нечего
  const isStartDisabled = lobby.players.length < 2 || lobby.players.length > 6
    || isFactionBanPhase
    || (!lobby.isRanked && lobby.players.some(player => !player.factionId));

  const isActingPlayerReady = !!actingPlayer?.factionId;

  // Выбирать не из чего, пока сервер не открыл игроку его четверку фракций:
  // в ручном рейтинговом лобби админа она скрыта до нажатия «Начать матч»
  const hasFactionsToSelect = !lobby.isRanked || (actingPlayer?.availableFactionIds || []).length > 0;
  // Подтвердивший выбор меняет фракцию только после «Изменить выбор»
  const isFactionSelectBlocked = isFactionBanPhase || !hasFactionsToSelect || isOwnFactionConfirmed;

  const selectFactionText = isActingPlayerReady ? 'Сменить фракцию' : 'Выбрать фракцию'

  // «Выбрать» в карточке — только там, где выбор вообще имеет смысл: баны уже
  // позади, выбор не подтвержден, иконка своя, и фракция не вычеркнута этими
  // самыми банами (и, во встроенном выборе десктопа, вообще открыта игроку)
  const isSelectFromCardAllowed = Boolean(factionCard)
    && !isFactionBanPhase
    && !isOwnFactionConfirmed
    && factionCard.playerId === networkId
    && factionCard.factionId !== actingPlayer?.bannedFactionId
    && factionCard.isSelectable !== false;

  const handleTimerCheckboxClick = (e) => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setTimerEnabled(e.target.checked)
    if (!e.target.checked) {
      setFastMode(false);
    }
  }
  const handleFastGameCheckboxClick = (e) => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setFastMode(e.target.checked)
    if (e.target.checked) {
      setTimerEnabled(true)
    }
  }

  // Рейтинговые лобби без создателя (авто-матч-мейкинг) недоступны для
  // ручного выхода никому из игроков — партия управляется сервером.
  // Исключение — админ, создавший рейтинговое лобби вручную из Админки:
  // он остается полноценным создателем и может закрыть лобби как раньше.
  // ИИ-лобби тоже ведет сервер, но собирается оно долго: выйти можно до
  // фазы банов (checkIfAiLobbyLeaveAllowed), а зрителю — всегда
  const isLeaveVisible = isAiLobby
    ? (isAiLobbySpectator || checkIfAiLobbyLeaveAllowed(lobby))
    : (!lobby.isRanked || isCreator);

  // Добавить бота: суперадмину в обычное лобби, админу — в рейтинговое, пока
  // не началась подготовка (баны и выбор фракции) — потом сервер откажет
  let addBotButton = null;
  if (isCreator && isSuperAdmin && !lobby.isRanked) {
    addBotButton = {buttonId: 'add_random_bot', onClick: handleAddRandomBot};
  } else if (isCreator && isAdminOrSuperAdmin && lobby.isRanked && !isRankedPreparationStarted) {
    addBotButton = {buttonId: 'add_random_bot_ranked', onClick: handleAddRankedBot};
  }

  const phaseHint = isFactionBanPhase
    ? (isDesktopLayout ? FACTION_BAN_HINT_DESKTOP : FACTION_BAN_HINT)
    : (isDesktopLayout ? FACTION_SELECT_HINT_DESKTOP : FACTION_SELECT_HINT);

  const lobbyModals = (
    <>
      {isInviteModalOpen && (
        <LobbyInviteModal
          // Пока код не доехал в профиль (сессия открыта до его появления) —
          // откат на id: серверный разбор принимает оба формата
          inviterReferalCode={auth.friendReferalCode || auth.id}
          lobbyId={lobbyId}
          onClose={() => setIsInviteModalOpen(false)}
        />
      )}

      {/* Монтируется только с открытой карточкой, хотя без фракции она и сама
          вернула бы null: карточка вешает на окно закрытие по Enter/Space (см.
          useCloseOnEnterSpace), а в лобби есть свои чекбоксы, которым пробел
          нужен самим */}
      {factionCard && (
        <FactionCardOverlay
          factionId={factionCard.factionId}
          skinId={factionCard.skinId}
          onClose={() => setFactionCard(null)}
          renderActionButton={isSelectFromCardAllowed ? (config) => (
            <PageButton
              // Свой звук выбора фракции вместо общего щелчка кнопки
              noSound
              onClick={() => handleSelectFactionFromCard(config.id)}
              pageId="LobbyPage"
              buttonId="select_faction_from_card"
              buttonValue={config.id}
            >
              Выбрать
            </PageButton>
          ) : undefined}
        />
      )}

      {kickConfirmPlayer && isCreator && (
        <LobbyKickConfirmModal
          playerName={kickConfirmPlayer.name}
          onConfirm={handleConfirmKickPlayer}
          onCancel={() => setKickConfirmPlayerId(null)}
        />
      )}

      {isPhaseHintOpen && (
        <RankedPhaseHintModal
          {...phaseHint}
          onHide={handleClosePhaseHint}
        />
      )}

      {fieldPlayersMismatchMessage && (
        <FieldPlayersMismatchModal
          message={fieldPlayersMismatchMessage}
          onHide={() => setFieldPlayersMismatchMessage(null)}
        />
      )}

      {aiSpectatorNoticeReason && isAiLobbySpectator && (
        <AiLobbySpectatorModal reason={aiSpectatorNoticeReason} onHide={() => setAiSpectatorNoticeReason(null)}/>
      )}
    </>
  );

  // Десктоп: крупные карточки игроков, встроенный выбор фракции артами и все
  // кнопки управления в нижней полосе (см. desktop/LobbyDesktop). Отдельного
  // экрана выбора фракции здесь нет — кнопки «Выбрать фракцию» тоже
  if (isDesktopLayout) {
    return (
      <LobbyDesktop
        lobby={lobby}
        players={players}
        networkId={networkId}
        actingPlayer={actingPlayer}
        auth={auth}
        isCreator={isCreator}
        isAiLobby={isAiLobby}
        countdown={{
          seconds: countdownSeconds,
          urgency: countdownUrgency,
          aiState: aiCountdownState,
          isBanPhase: isFactionBanPhase,
          hintTitle: getHintTitleAttribute(phaseHint),
          onOpenHint: handleOpenPhaseHint,
        }}
        banView={{
          isPhase: isFactionBanPhase,
          isVoter: isBanVoter,
          isOwnConfirmed: isOwnBanConfirmed,
          isVoteAllowed: isOwnBanVoteAllowed,
          ownVotes: ownBanVotes,
          confirmedCount: banConfirmedCount,
          voterCount: banVoterIds.length,
          missingTargetNames: missingBanTargetNames,
          isConfirmDisabled: isBanConfirmDisabled,
          getPlayerStatus: getPlayerBanStatus,
          getFactionVoters: getFactionBanVoters,
        }}
        selectView={{
          isWindow: isRankFactionSelectWindow,
          isConfirmer: isFactionConfirmer,
          isOwnConfirmed: isOwnFactionConfirmed,
          hasOwnFaction,
          isConfirmDisabled: isFactionConfirmDisabled,
          readyCount: factionSelectReadyCount,
          playerCount: players.length,
          checkIfPlayerReady,
        }}
        ownFactions={{
          isSelectBlocked: isFactionSelectBlocked,
          selectFlash: factionSelectFlash,
          onSelect: handleSelectFactionInline,
          onTileSelect: handleOwnTileSelectClick,
          onOpenOwnCard: openOwnFactionCard,
          onGoToCrystals,
          onOpenInventory,
        }}
        onBanVoteClick={handleBanVoteClick}
        onOpenFactionCard={openFactionCard}
        onKickPlayer={handleKickPlayer}
        searchingHint={isSearchingHintVisible ? {count: searchingCount, avgSeconds: searchingAvgSeconds} : null}
        controls={{
          isLeaveVisible,
          onLeave: handleLeave,
          isInviteVisible: !lobby.isRanked,
          onInvite: () => setIsInviteModalOpen(true),
          isCreatorSettingsVisible: isCreator && !lobby.isRanked,
          timerEnabled,
          fastMode,
          onTimerChange: handleTimerCheckboxClick,
          onFastModeChange: handleFastGameCheckboxClick,
          isBonusVisible: !lobby.isRanked,
          onSelectLobbyBonus: handleSelectLobbyBonus,
          isFieldSelectVisible: isCreator && isAdminOrSuperAdmin,
          onOpenFieldSelect: handleOpenFieldSelect,
          addBotButton,
          onConfirmBans: handleConfirmFactionBans,
          onConfirmFactionSelection: handleConfirmFactionSelection,
          isStartVisible: isCreator,
          isStartDisabled,
          onStartGame: handleStartGame,
        }}
      >
        {lobbyModals}
      </LobbyDesktop>
    );
  }

  return (
    <div className={'lobby-page-container'}>
      {/* Картинка уже загружена (см. shownOwnFactionArtPath): смена src у того
          же элемента подменяет ее сразу, проявление — только при появлении.
          Без decoding="async": с ним Chrome при смене src на еще не
          отрисованную картинку показал бы кадр-другой пустоты */}
      {shownOwnFactionArtPath && (
        <img
          className={'lobby_selected-faction-image'}
          src={shownOwnFactionArtPath}
          alt={''}
        />
      )}

      {/* Полоса под арт выбранной фракции: таймер прижат к ее низу. Пока арта
          нет (фракция не выбрана или арты своих фракций еще грузятся), полоса
          сжата до плашки отсчета, и список игроков сразу под ней; появился
          арт — она плавно раскрывает место под него. Высоту содержимого
          полоса знает по замеру — и к ней тоже подстраивается плавно, а не
          скачком (см. .lobby-countdown-slot) */}
      <LobbyCountdownSlot
        className={`lobby-countdown-slot ${shownOwnFactionArtPath ? 'lobby-countdown-slot_with-art' : ''} ${countdownSlotContentHeight !== null ? 'lobby-countdown-slot_measured' : ''}`}
        style={countdownSlotContentHeight !== null
          ? {'--lobby-countdown-slot-content-height': `${countdownSlotContentHeight}px`}
          : undefined}
        isWithArt={Boolean(shownOwnFactionArtPath)}
        contentHeight={countdownSlotContentHeight}
      >
        <div className="lobby-countdown-slot-content" ref={countdownSlotContentRef}>
          {/* Заголовок — пока над списком нет арта своей фракции: появился
              арт — место заголовка занимает он. Текст тот же, что в шапке
              десктопа */}
          {!shownOwnFactionArtPath && (
            <div className="lobby-mobile-heading">
              <h1 className="lobby-mobile-heading_title">{getLobbyTitle(lobby, isAiLobby)}</h1>
              <div className="lobby-mobile-heading_subtitle">{getLobbySubtitle(lobby, players.length)}</div>
            </div>
          )}
          {countdownSeconds === null && aiCountdownState && (
            // ИИ-лобби до фазы банов: тот же отсчет, что на кнопке меню и в
            // промо-модалке, — «Сбор игроков» до 21:00, затем «Старт через» (по
            // часам сервера)
            <div className="lobby-rank-match-countdown">
              {aiCountdownState.prefix} <span className="text-attention">{aiCountdownState.timeText}</span>
            </div>
          )}
          {countdownSeconds !== null && (
            <div className={`lobby-rank-match-countdown lobby-rank-match-countdown_stacked lobby-countdown_${countdownUrgency}`}>
              <div className="lobby-countdown-line">
                {isFactionBanPhase ? (
                  <>Бан фракций: <span className="text-attention">{countdownSeconds}</span> сек</>
                ) : (
                  <>Матч начнется через <span className="text-attention">{countdownSeconds}</span> сек</>
                )}
                {/* Места на правила в плашке нет, а прочитать их нужно хотя бы
                    раз: по наведению — подсказка браузера, по нажатию — модалка
                    с тем же текстом (на тачскрине наведения не бывает) */}
                <img
                  className="lobby-countdown-info-icon"
                  src="/img/icons/info.png"
                  alt="Правила фазы"
                  title={getHintTitleAttribute(phaseHint)}
                  onClick={handleOpenPhaseHint}
                />
              </div>
              {/* Прогресс — второй строкой той же плашки: отсчет и «сколько
                  осталось дождаться» читаются вместе. Баны закроются досрочно,
                  когда подтвердят все голосующие, матч стартует досрочно,
                  когда готовы все */}
              {isFactionBanPhase && banVoterIds.length > 0 && (
                <div className="lobby-countdown-progress">
                  Подтвердили: <span className="lobby-countdown-progress_count">{banConfirmedCount}</span> из {banVoterIds.length}
                </div>
              )}
              {isRankFactionSelectWindow && players.length > 0 && (
                <div className="lobby-countdown-progress">
                  Готовы: <span className="lobby-countdown-progress_count">{factionSelectReadyCount}</span> из {players.length}
                </div>
              )}
            </div>
          )}
        </div>
      </LobbyCountdownSlot>

      <div
        className={`lobby-players-list`}
      >
        {players.map((player) => (
          <div
            key={player.id}
            className={`lobby-player-item ${player.id === networkId ? 'lobby-acting-player' : ''}`}
          >
            <div className="lobby-player-info">
              <LobbyPlayerName player={player}/>
              <div className="lobby-player-badges">
                {player.status === 'offline' && <span className="lobby-status-badge">Оффлайн</span>}
                {player.id === lobby.creatorId && <span className="lobby-creator-badge">Создатель</span>}
                {player.id === networkId && <span className="lobby-you-badge">Вы</span>}
                {checkIfPlayerReady(player) && <span className="lobby-ready-badge">Готов</span>}
                {/* Короче, чем на десктопе («Выбирает фракцию»): метка стоит в
                    одной строке с ником и другими бейджами — длинная съела бы
                    ник (см. LobbyBanStatusBadge). В обычном лобби — как на
                    десктопе: у соперников, пока не выбрали фракцию (себе —
                    нет: свой выбор и так перед глазами, кнопкой) */}
                {(isRankFactionSelectWindow || (!lobby.isRanked && player.id !== networkId))
                  && !checkIfPlayerReady(player) && (
                  <span className="lobby-choosing-badge" title="Игрок еще не выбрал или не подтвердил фракцию">
                    Выбирает
                  </span>
                )}
                <LobbyBanStatusBadge status={getPlayerBanStatus(player)} isCompact/>
                {isCreator && player.id !== networkId && (
                  <PageButton
                    className="lobby-kick-button"
                    onClick={() => handleKickPlayer(player.id)}
                    title="Исключить игрока"
                    pageId="LobbyPage"
                    buttonId="kick_player"
                  >
                    <div
                      className={'lobby-kick-button_img'}
                    />
                  </PageButton>
                )}
              </div>
            </div>

            {lobby.isRanked && player.availableFactionIds && player.availableFactionIds.length > 0 && (
              <div className="lobby-player-factions">
                {player.availableFactionIds.map((factionId) => {
                  const selectedSkinId = player.selectedSkins?.[factionId];
                  // Банить можно только чужие фракции и только пока идет фаза
                  // Пока идут баны, одиночное нажатие по чужой иконке — голос,
                  // и карточку открывает только двойное. Когда баны позади,
                  // голосовать уже не за что: карточка открывается обычным
                  // нажатием по любой иконке, хоть своей, хоть чужой
                  // Голосует только голосующий (не бот со значком, не ИИ и не
                  // зритель ИИ-лобби) и только пока не подтвердил свои баны
                  const isBanVoteAllowed = isOwnBanVoteAllowed && player.id !== networkId;
                  const isIconInteractive = isBanVoteAllowed || !isFactionBanPhase;
                  // Пока фаза идет, значок показывает СВОЙ голос — красным
                  // контуром, потому что его еще можно снять (чужие голоса —
                  // счетчиком в углу). После нее — общий итог сервера, включая
                  // бан самому себе, и значок уже черный: решение окончательное
                  const isBanned = isFactionBanPhase
                    ? ownBanVotes[player.id] === factionId
                    : player.bannedFactionId === factionId;
                  const banVoters = getFactionBanVoters(player.id, factionId);
                  // Свою выбранную фракцию игрок видит подсвеченной в своей
                  // плашке. Только свою: пик соперника до старта — тайна
                  const isOwnSelected = player.id === networkId && player.factionId === factionId;
                  // Срезанные углы — только у иконки без рамки: рамку рейтингового
                  // образа рисовали под прямые углы, и срез под ней торчал бы
                  const isFramed = Boolean(getRankSkinFramePath(selectedSkinId));
                  // Обертка здесь, а не внутри PlayerFactionIcon: иконка общая
                  // для всего приложения, а счетчик голосов нужен только лобби
                  return (
                    <div key={factionId} className="lobby-player-faction-vote">
                      <PlayerFactionIcon
                        player={player}
                        factionId={factionId}
                        skinId={selectedSkinId}
                        className={`lobby-player-faction-icon ${isFramed ? 'lobby-player-faction-icon_framed' : ''} ${isIconInteractive ? 'lobby-player-faction-icon_interactive' : ''} ${isOwnSelected ? 'lobby-player-faction-icon_selected' : ''}`}
                        imgClassName="lobby-player-faction-icon-img"
                        // Картинка закрывает подложку целиком, и цвет игрока виден был бы
                        // только в срезанных углах — цветными треугольниками
                        backgroundColor="transparent"
                        isBanned={isBanned}
                        isBanFinal={!isFactionBanPhase}
                        banTitle={isFactionBanPhase
                          ? (isOwnBanVoteAllowed ? 'Ваш голос за бан — нажмите, чтобы снять' : 'Ваш голос за бан')
                          : 'Фракция забанена'}
                        onClick={isBanVoteAllowed
                          ? () => handleBanVoteClick(player.id, factionId)
                          : (isIconInteractive ? () => openFactionCard(player, factionId) : undefined)}
                        // Карточку фракции открывает любая иконка — и своя, и чужая
                        onDoubleClick={() => openFactionCard(player, factionId)}
                        // Значок «явного» бота — один, рядом с никнеймом (LobbyPlayerName)
                        hideVisibleBotMark
                      />
                      {banVoters.length > 0 && (
                        <span
                          className="lobby-faction-vote-count"
                          title={`Голоса за бан: ${banVoters.map((voter) => voter.name).join(', ')}`}
                        >
                          {banVoters.length}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Зритель ИИ-лобби: мест нет, фракцию не выбирать — матч он
            увидит, когда тот начнется */}
        {isAiLobbySpectator && (
          <div className="lobby-search-hint lobby-spectator-plaque">Вы зритель</div>
        )}

        {/* Создатель один в лобби: плашка-подсказка о поиске игроков.
            Скрывается, как только присоединяется хотя бы один игрок (бот или человек).
            В ИИ-лобби ее нет (см. isSearchingHintVisible) */}
        {isSearchingHintVisible && (
          <div className="lobby-search-hint">
            <span>Игроков сейчас ищет лобби:</span>
            <span className={'text-attention'}>{searchingCount}</span>
            <span>Среднее время присоединения:</span>
            <span className={'text-attention'}>{searchingAvgSeconds} сек</span>
          </div>
        )}
      </div>

      <div className="main-menu-buttons lobby-page-buttons">
        {/* Свои баны выбраны — подтверждение; подтвердили все голосующие —
            фаза закрывается досрочно. Снять подтверждение («Изменить баны»)
            можно, пока фаза идет */}
        {isBanVoter && (
          <PageButton
            className={`lobby-button ${!isOwnBanConfirmed && !isBanConfirmDisabled ? 'lobby-button-pulse' : ''}`}
            onClick={() => handleConfirmFactionBans(!isOwnBanConfirmed)}
            disabled={isBanConfirmDisabled}
            pageId="LobbyPage"
            buttonId={isOwnBanConfirmed ? 'edit_faction_bans' : 'confirm_faction_bans'}
            // Акцентный цвет — инлайном, как у «Выбрать фракцию»: класс
            // .lobby-start-button перебивается фоном .main-menu-button
            style={{
              backgroundColor: (isOwnBanConfirmed || isBanConfirmDisabled) ? undefined : PRIMARY_COLOR,
            }}
          >
            {isOwnBanConfirmed ? 'Изменить баны' : 'Подтвердить баны'}
          </PageButton>
        )}
        {isCreator && (
          <>
            <PageButton
              className={`lobby-button lobby-start-button ${!isStartDisabled ? 'lobby-button-pulse' : ''}`}
              onClick={handleStartGame}
              disabled={isStartDisabled}
              pageId="LobbyPage"
              buttonId="start_game"
              buttonValue={`players_count_${lobby.players.length}`}
            >
              Начать матч
            </PageButton>
            {isAdminOrSuperAdmin && (
              <PageButton
                className="lobby-button"
                onClick={handleOpenFieldSelect}
                pageId="LobbyPage"
                buttonId="select_field"
              >
                Выбрать поле
              </PageButton>
            )}
            {addBotButton && (
              <PageButton
                className="lobby-button"
                onClick={addBotButton.onClick}
                pageId="LobbyPage"
                buttonId={addBotButton.buttonId}
              >
                Добавить бота
              </PageButton>
            )}
          </>
        )}
        {/* Пока идут баны (и пока в ручном рейтинговом лобби фракции еще не
            открыты), выбирать рано — и кнопка не должна звать нажатием
            (пульс и цвет): иначе заблокированная зазывает впустую.
            Зрителю ИИ-лобби выбирать нечего вовсе */}
        {!isAiLobbySpectator && (
          <PageButton
            className={`lobby-button ${!isActingPlayerReady && !isFactionSelectBlocked ? 'lobby-button-pulse' : ''}`}
            onClick={handleSelectFaction}
            disabled={isFactionSelectBlocked}
            pageId="LobbyPage"
            buttonId="select_faction"
            style={{
              backgroundColor: (isActingPlayerReady || isFactionSelectBlocked) ? undefined : PRIMARY_COLOR,
            }}
          >
            {selectFactionText}
          </PageButton>
        )}
        {/* Окно выбора рейтингового лобби: выбор фиксирует подтверждение;
            подтвердили все — матч стартует досрочно. Снять подтверждение
            («Изменить выбор») можно до старта. Акцентный цвет — инлайном, как
            у «Подтвердить баны» (класс перебивается фоном .main-menu-button) */}
        {isFactionConfirmer && (
          <>
            <PageButton
              className={`lobby-button ${!isOwnFactionConfirmed && !isFactionConfirmDisabled ? 'lobby-button-pulse' : ''}`}
              onClick={() => handleConfirmFactionSelection(!isOwnFactionConfirmed)}
              disabled={isFactionConfirmDisabled}
              pageId="LobbyPage"
              buttonId={isOwnFactionConfirmed ? 'edit_faction_selection' : 'confirm_faction_selection'}
              style={{
                backgroundColor: (isOwnFactionConfirmed || isFactionConfirmDisabled) ? undefined : PRIMARY_COLOR,
              }}
            >
              {isOwnFactionConfirmed ? 'Изменить выбор' : 'Подтвердить выбор'}
            </PageButton>
            {!isOwnFactionConfirmed && !hasOwnFaction && (
              <div className="lobby-confirm-hint">Сначала выберите фракцию</div>
            )}
          </>
        )}
        {/* Приглашение по ссылке — только в обычных сетевых лобби: состав
            рейтинговых собирает матч-мейкинг сервера, звать туда друга
            бессмысленно. Кнопка есть у ЛЮБОГО участника лобби, не только у
            создателя: ссылка ведет в это же лобби, а реферальная метка в ней —
            того, кто приглашает. Проверять статус лобби не нужно: с началом
            матча сервер шлет LOAD_STATE, и приложение уходит с этого экрана
            на игровой (см. onGameStart) */}
        {!lobby.isRanked && (
          <PageButton
            className="lobby-button"
            onClick={() => setIsInviteModalOpen(true)}
            pageId="LobbyPage"
            buttonId="invite_friend"
          >
            Пригласить друга
          </PageButton>
        )}

        {/* Кому можно выйти — см. isLeaveVisible */}
        {isLeaveVisible && (
          <PageButton className="lobby-button" onClick={handleLeave} pageId="LobbyPage" buttonId="leave_lobby">
            Покинуть лобби
          </PageButton>
        )}

        {isCreator && (
          <>
            {!lobby.isRanked && (
              <>
                <label className="lobby-checkbox lobby-timer-checkbox">
                  <input
                    type="checkbox"
                    checked={timerEnabled}
                    onChange={handleTimerCheckboxClick}
                  />
                  <span>Автоматический таймер ходов</span>
                </label>
                <label className="lobby-checkbox">
                  <input
                    type="checkbox"
                    checked={fastMode}
                    onChange={handleFastGameCheckboxClick}
                  />
                  <span>Быстрый режим</span>
                </label>
              </>
            )}
          </>
        )}

        {/* Бонус обычного лобби действует на всех игроков, поэтому виден всем
            участникам, а переключает его только создатель. В рейтинговых
            лобби бонуса нет */}
        {!lobby.isRanked && (
          <LobbyBonusToggle
            bonusType={lobby.bonusType}
            isCreator={isCreator}
            onSelect={handleSelectLobbyBonus}
          />
        )}
      </div>

      {lobbyModals}
    </div>
  );
}

export default LobbyPage;
